import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import criticalCss from "../critical.css?inline";
import urbanistFont from "../assets/fonts/urbanist-latin-wght-normal.woff2?url";
import epilogueFont from "../assets/fonts/epilogue-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { DeferredCookieConsent } from "../components/DeferredCookieConsent";
import { socialProfileUrls } from "../lib/data";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Pagina niet gevonden</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          De pagina die je zoekt bestaat niet of is verplaatst.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Naar de homepagina
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Deze pagina laadt niet
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Er ging iets mis aan onze kant. Probeer te vernieuwen of ga terug naar home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Opnieuw proberen
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Naar de homepagina
          </a>
        </div>
      </div>
    </div>
  );
}

// Structured data for local SEO — rendered on every page.
const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  "@id": "https://www.fedortimmerwerken.nl/#business",
  name: "Fedor Timmerwerken",
  alternateName: "Timmerbedrijf Fedor Timmerwerken Dordrecht",
  description:
    "Timmer- en aannemersbedrijf in Dordrecht: dakkapellen, kozijnen, aanbouwen, renovaties, vlonders en schuren — ter plekke op maat gemaakt.",
  url: "https://www.fedortimmerwerken.nl/",
  image:
    "https://www.fedortimmerwerken.nl/__l5e/assets-v1/c1a3ce21-80a3-46a0-ad35-f7c734ff4487/og-default.jpg",
  telephone: "+31628130786",
  email: "info@fedortimmerwerken.nl",
  priceRange: "€€",
  currenciesAccepted: "EUR",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sikkelstraat 50",
    postalCode: "3319 LJ",
    addressLocality: "Dordrecht",
    addressRegion: "Zuid-Holland",
    addressCountry: "NL",
  },
  geo: { "@type": "GeoCoordinates", latitude: 51.7913, longitude: 4.6795 },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "17:00",
    },
  ],
  foundingDate: "2018",
  founder: { "@type": "Person", name: "Fedor de Vries" },
  sameAs: socialProfileUrls,

  areaServed: [
    { "@type": "City", name: "Dordrecht" },
    { "@type": "City", name: "Zwijndrecht" },
    { "@type": "City", name: "Papendrecht" },
    { "@type": "City", name: "Sliedrecht" },
    { "@type": "City", name: "Hendrik-Ido-Ambacht" },
  ],
  knowsAbout: [
    "dakkapel plaatsen",
    "kozijnen vervangen",
    "aanbouw bouwen",
    "verbouwing en renovatie",
    "vlonders en schuren",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Timmerwerk en verbouwingen",
    itemListElement: [
      "Dakkapellen",
      "Kozijnen",
      "Aanbouwen",
      "Renovaties",
      "Vlonders en schuren",
    ].map((naam) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: naam, areaServed: "Dordrecht" },
    })),
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "43",
  },
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      // Kleur van de browserbalk op telefoons; ThemeToggle past hem live aan.
      { name: "theme-color", content: "#fafafa" },
      { name: "author", content: "Fedor Timmerwerken" },
      // Eigendomsverificatie voor Google Search Console.
      {
        name: "google-site-verification",
        content: "FyX59KxBIoQ9HxLrs2RHG6LWSEQBZN1YvYpAGFsylQo",
      },
      {
        name: "google-site-verification",
        content: "pouh3r4rNFUwaJ-hQKEyl57PVFM8hRq8ewlhkiOMU24",
      },
      { property: "og:site_name", content: "Fedor Timmerwerken" },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary_large_image" },
      // Titel, description, og:*/twitter:* per pagina staan in de leaf-routes,
      // zodat er geen dubbele of conflicterende tags ontstaan.

    ],
    links: [
      // Hoofd-CSS render-blocking laden. Async laden (preload + promotie)
      // veroorzaakte een grote layout shift (CLS ~0.9) omdat de pagina eerst
      // met alleen de critical CSS schilderde en daarna opnieuw indeelde.
      {
        rel: "stylesheet",
        href: appCss,
      },

      // Zelf gehoste fonts: preload direct de twee latin woff2-bestanden
      // (de rest volgt via unicode-range alleen als het nodig is).
      {
        rel: "preload",
        href: urbanistFont,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: epilogueFont,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },

      // Officiële Fedor Timmerwerken favicon-set.
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(localBusinessJsonLd),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Zet het opgeslagen thema toe vóór eerste paint (voorkomt licht/donker-flash)
// en synct meta[name=theme-color].
const themeInitScript = `(function(){try{var dark=localStorage.getItem("fedor-theme")==="dark";if(dark){document.documentElement.classList.add("dark")}var m=document.querySelector('meta[name="theme-color"]');if(m){m.setAttribute("content",dark?"#232323":"#fafafa")}}catch(e){}})();`;

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="nl" suppressHydrationWarning>
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: criticalCss }} />
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
      </head>

      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Ondermarge op mobiel zodat de vaste actiebalk nooit content dekt. */}
      <div className="flex min-h-screen flex-col bg-background pb-[calc(4.75rem+env(safe-area-inset-bottom))] text-foreground md:pb-0">
        <a href="#main-content" className="skip-link">
          Naar hoofdinhoud
        </a>
        <Header />
        <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
          <Outlet />
        </main>
        <Footer />
        <WhatsAppButton />
        <MobileActionBar />
        <DeferredCookieConsent />
      </div>
    </QueryClientProvider>
  );
}
